import {
  PayloadAction,
  SerializedError,
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit'
import { Booking } from '../models/types'
import { AppDispatch, RootState } from '../store'
import { findExternalBookings, ExternalBooking, Screens } from 'patient-api-lib'
import { DateTime } from 'luxon'
import { createDateFromObject } from '../util/date'
import { isPast } from 'date-fns'
import { navigateBackTo } from './screensRouterSlice'
import { done as yourBookingsSliceDone } from './yourBookingsSlice'
import { callException } from './appConfigSlice'

const createBookingObject = (booking: ExternalBooking): Booking => {
  return {
    id: booking.getId() ?? '',
    treatmentName: booking.getTreatmentName()?.getValue() ?? '',
    clinicName: booking.getClinicName()?.getValue() ?? '',
    reschedulable: booking.getReschedulable() ?? false,
    treatmentId: booking.getTreatmentId()?.getValue() ?? '',
    clinicId: booking.getClinicId() ?? '',
    connectionId: booking.getConnectionId() || '',
    careGiverName: booking.getCaregiverName()?.getValue() ?? '',
    startTime: createDateFromObject(booking.getStartTime()?.toObject()).toISO(),
    endTime: createDateFromObject(booking.getEndTime()?.toObject()).toISO(),
  }
}

export interface FilteredBookings {
  pastBookings: Booking[] | null
  futureBookings: Booking[] | null
}

type YourBookingScreenState = {
  selectedBooking: Booking | null
  loading: boolean
  error: SerializedError | null
  bookings: FilteredBookings
}

export const initialState: YourBookingScreenState = {
  selectedBooking: null,
  loading: false,
  error: null,
  bookings: {
    pastBookings: null,
    futureBookings: null,
  },
}

export const getExternalBookings = createAsyncThunk<
  FilteredBookings,
  void,
  { dispatch: AppDispatch; state: RootState }
>('yourBookingsScreen/getExternalBookings', async (_, thunkApi) => {
  const state = thunkApi.getState()
  const jwt = state.myBookingsScreen.jwt
  const clinicIds = state.app.clinicIds
  const bookingId = state.app.bookingId
  const selectedBooking = state.yourBookingsScreen.selectedBooking

  if (!jwt) throw new Error('Unauthenticated: jwt is missing')

  const today = DateTime.now()
  const dateFrom = today.minus({ month: 36 }).toJSDate()
  const dateTo = today.plus({ month: 12 }).toJSDate()

  const response = await findExternalBookings(
    jwt,
    clinicIds,
    dateFrom,
    dateTo,
    '1',
    undefined
  )

  // TODO!: Handle unauthorized response message
  const bookings = response
    .getExternalBookingsList()
    .map((b) => createBookingObject(b))

  const filteredBookings = bookings.reduce<FilteredBookings>(
    (acc, booking) => {
      const key = isPast(new Date(booking.startTime))
        ? 'pastBookings'
        : 'futureBookings'
      return {
        ...acc,
        [key]: [...(acc[key] || []), booking],
      }
    },
    { pastBookings: [], futureBookings: [] }
  )

  const sortedFutureBookings = (filteredBookings.futureBookings ?? []).sort(
    (a, b) => {
      const dateA = DateTime.fromISO(a.startTime || '').toMillis()
      const dateB = DateTime.fromISO(b.startTime || '').toMillis()
      return dateB - dateA
    }
  )

  const sortedPastBookings = (filteredBookings.pastBookings ?? []).sort(
    (a, b) => {
      const dateA = DateTime.fromISO(a.startTime || '').toMillis()
      const dateB = DateTime.fromISO(b.startTime || '').toMillis()
      return dateB - dateA
    }
  )

  if (bookingId && !selectedBooking) {
    if (!sortedFutureBookings && !sortedPastBookings)
      throw new Error('No bookings found')

    const selectedBooking = [
      ...(sortedFutureBookings || []),
      ...(sortedPastBookings || []),
    ].find((booking) => booking.id === bookingId)

    if (!selectedBooking) {
      thunkApi.dispatch(
        callException({
          message: 'Booking not found',
          showButton: true,
          errorType: 'booking-not-found',
        })
      )
      return {
        pastBookings: sortedPastBookings,
        futureBookings: sortedFutureBookings,
      }
    }

    // FIX issue with caregiver name in preview environment
    if (import.meta.env.VITE_ENVIRONMENT === 'preview') {
      const careGiverMapping: { [key: string]: string } = {
        ['Albin Jonsson']: 'Roma Sunichuk',
        ['Lisbeth Bergman']: 'Niels Rask',
      }

      thunkApi.dispatch(
        yourBookingsSliceDone({
          ...selectedBooking,
          careGiverName:
            careGiverMapping[selectedBooking.careGiverName] ||
            selectedBooking.careGiverName,
        })
      )
    } else {
      await thunkApi.dispatch(yourBookingsSliceDone(selectedBooking))
    }
  }

  return {
    pastBookings: sortedPastBookings,
    futureBookings: sortedFutureBookings,
  }
})

export const done = createAsyncThunk<
  Screens,
  Booking,
  { dispatch: AppDispatch; state: RootState }
>('yourBookingsScreen/done', async (booking, thunkApi) => {
  thunkApi.dispatch(setLoading(true))
  thunkApi.dispatch(setSelectedBooking(booking))
  return Screens.BookingDetailsScreen
})

export const YourBookingsScreenSlice = createSlice({
  name: 'yourBookingsScreenSlice',
  initialState,
  reducers: {
    setSelectedBooking: (state, action: PayloadAction<Booking | null>) => {
      state.selectedBooking = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getExternalBookings.pending, (state) => {
      state.loading = true
      state.bookings = {
        pastBookings: null,
        futureBookings: null,
      }
    })
    builder.addCase(getExternalBookings.rejected, (state, action) => {
      state.loading = false
      state.error = action.error

      if (state.error.message === 'Unauthenticated') {
        navigateBackTo(Screens.MyBookingsScreen)
      }
    })
    builder.addCase(getExternalBookings.fulfilled, (state, action) => {
      state.loading = false
      state.bookings = action.payload
    })
  },
})

export const { setSelectedBooking, setLoading } =
  YourBookingsScreenSlice.actions

export const getSelectedBooking = (state: RootState) =>
  state.yourBookingsScreen.selectedBooking

export const getBookings = (state: RootState) =>
  state.yourBookingsScreen.bookings

export default YourBookingsScreenSlice.reducer
